/**
 * Utility for consistent stake color assignment across components
 * Ensures badges, histograms, and summaries use the same color palette
 */

// Define color palette for stakes (matching Bootstrap theme)
const STAKE_COLORS = [
  {
    background: "rgba(94, 114, 228, 0.7)", // Blue (#5e72e4)
    border: "rgba(94, 114, 228, 1)",
    solid: "#5e72e4",
  },
  {
    background: "rgba(70, 148, 8, 0.7)", // Green (#469408)
    border: "rgba(70, 148, 8, 1)",
    solid: "#469408",
  },
  {
    background: "rgba(217, 131, 31, 0.7)", // Orange (#D9831F)
    border: "rgba(217, 131, 31, 1)",
    solid: "#D9831F",
  },
  {
    background: "rgba(137, 101, 224, 0.7)", // Purple (#8965e0)
    border: "rgba(137, 101, 224, 1)",
    solid: "#8965e0",
  },
  {
    background: "rgba(2, 154, 207, 0.7)", // Cyan (#029ACF)
    border: "rgba(2, 154, 207, 1)",
    solid: "#029ACF",
  },
  {
    background: "rgba(32, 201, 151, 0.7)", // Teal (#20c997)
    border: "rgba(32, 201, 151, 1)",
    solid: "#20c997",
  },
  {
    background: "rgba(217, 35, 15, 0.7)", // Red (#D9230F)
    border: "rgba(217, 35, 15, 1)",
    solid: "#D9230F",
  },
];

/**
 * Get background colors for histogram bars
 */
export const getStakeBackgroundColors = (): string[] => {
  return STAKE_COLORS.map((c) => c.background);
};

/**
 * Get border colors for histogram bars
 */
export const getStakeBorderColors = (): string[] => {
  return STAKE_COLORS.map((c) => c.border);
};

/**
 * Get solid colors for badges and progress bars
 */
export const getStakeSolidColors = (): string[] => {
  return STAKE_COLORS.map((c) => c.solid);
};

/**
 * Get color for a specific stake by its index in the sorted stake array
 * @param index - The index of the stake in the sorted stakes array
 * @param variant - The color variant to return ('solid', 'background', or 'border')
 */
export const getStakeColorByIndex = (
  index: number,
  variant: "solid" | "background" | "border" = "solid"
): string => {
  const colorIndex = index % STAKE_COLORS.length;
  return STAKE_COLORS[colorIndex][variant];
};

/**
 * Get color for a specific stake value from a sorted stakes array
 * @param stake - The stake value to find
 * @param sortedStakes - Array of sorted unique stake values
 * @param variant - The color variant to return
 */
export const getStakeColor = (
  stake: string,
  sortedStakes: string[],
  variant: "solid" | "background" | "border" = "solid"
): string => {
  const index = sortedStakes.indexOf(stake);
  if (index === -1) {
    // Default to first color if stake not found
    return STAKE_COLORS[0][variant];
  }
  return getStakeColorByIndex(index, variant);
};
